import { useQuery, type QueryKey, type UseQueryResult } from '@tanstack/react-query'

import type { RequestOptions } from './client.ts'
import type { ApiError } from './errors.ts'

/**
 * 조회 훅의 **유일한 생성자**.
 *
 * 🔴 옵션을 열어 두지 않는다. `refetchInterval` 같은 폴링 축을 호출부가 켤 수 없게
 *    **받는 것이 키와 페처뿐**이다. 갱신은 구독 → 무효화로만 온다(DP-01).
 * 🔴 `enabled` 만 예외로 받는다 — 상세 화면이 id 를 모를 때 요청을 안 보내야 하기 때문이고,
 *    이건 「더 자주 부르기」가 아니라 「덜 부르기」다. 같은 이유로 호출부는 `skip` 만 넘길 수 있다
 *    (데이터 소스가 `demo` 인 도메인은 실물을 때리지 않는다).
 */

/** 모든 조회 훅이 공통으로 받는 축. **끄는 것만 된다** */
export interface SkippableArgs {
  /** `true` 면 요청을 보내지 않는다. 데이터 소스가 `demo` 일 때 컨테이너가 쓴다 */
  skip?: boolean
}
export interface QueryDefinition<TArgs, TData> {
  key: (args: TArgs) => QueryKey
  fetch: (args: TArgs) => Promise<TData>
  /** 인자가 덜 찼을 때 요청을 막는다. 기본은 항상 켬 */
  enabled?: (args: TArgs) => boolean
}

/**
 * 훅 인자 → 질의 문자열. **`skip` 은 우리 축이라 서버로 보내지 않는다.**
 * (보내면 서버가 모르는 파라미터라 422 를 줄 수 있다)
 */
export function queryParams<T extends object>(args: T): NonNullable<RequestOptions['query']> {
  const rest: Record<string, unknown> = { ...(args as Record<string, unknown>) }
  delete rest['skip']
  return rest as NonNullable<RequestOptions['query']>
}

export type QueryHook<TArgs, TData> = (args: TArgs) => UseQueryResult<TData, ApiError>

export function createQuery<TArgs, TData>(
  definition: QueryDefinition<TArgs, TData>,
): QueryHook<TArgs, TData> {
  return function useApiQuery(args: TArgs) {
    return useQuery<TData, ApiError>({
      queryKey: definition.key(args),
      queryFn: () => definition.fetch(args),
      enabled:
        (args as SkippableArgs | undefined)?.skip !== true && (definition.enabled?.(args) ?? true),
    })
  }
}
